/**
 * WelcomePanel: closeable orientation card shown once in the lobby's left
 * column right after a player creates their first character this session
 * (`justCreatedCharacter`, set alongside the hero overlay at creation
 * stream-end). Used to show on every pre-character visit instead, which
 * front-loaded a wall of orientation copy onto the startup screen before a
 * player had done anything; the post-creation moment is a better fit for it.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sparkles, X } from 'lucide-react';
import { DISCORD_INVITE_URL } from '@/lib/constants';
import { track } from '@/lib/analytics';
import { useLobbyStore } from '@/lib/stores/lobbyStore';

export default function WelcomePanel() {
  const justCreatedCharacter = useLobbyStore((state) => state.justCreatedCharacter);
  const setJustCreatedCharacter = useLobbyStore((state) => state.setJustCreatedCharacter);

  if (!justCreatedCharacter) return null;

  return (
    // select-none: without it, dragging across the panel paints a text-selection
    // overlay that reads as a hover state (same fix as the friends-panel rows).
    <Card data-testid="welcome-panel" className="shrink-0 select-none">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent-gold" />
            Welcome to Polymyth
          </CardTitle>
          <button
            type="button"
            aria-label="Dismiss"
            className="rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
            onClick={() => setJustCreatedCharacter(false)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-1 space-y-2 text-base leading-snug text-foreground">
        <ul className="list-disc space-y-0.5 pl-5">
          <li>Adventure with your character solo or with friends</li>
          <li>A 5th edition compatible engine runs the game</li>
          <li>You don&apos;t need to know any rules to play</li>
          <li>Level up your character and keep it between adventures</li>
          <li>
            Join the{' '}
            <a
              href={DISCORD_INVITE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-accent-gold hover:underline"
            >
              community
            </a>{' '}
            to connect with other players
          </li>
          <li>
            Learn more about{' '}
            <a
              href="/multiplayer"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-accent-gold hover:underline"
              onClick={() => track('multiplayer_promo_click', { surface: 'welcome_panel' })}
            >
              playing with friends
            </a>
          </li>
        </ul>
      </CardContent>
    </Card>
  );
}
